import { useEffect, useRef } from 'react';

type UseInViewTriggerProps = {
  onEnter?: () => void;
  enabled?: boolean;
  once?: boolean;
  threshold?: number;
  rootMargin?: string;
};

export const useInViewTrigger = <T extends Element>({
  onEnter,
  enabled = true,
  once,
  threshold = 0,
  rootMargin = '0px',
}: UseInViewTriggerProps) => {
  const ref = useRef<T | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          if (once) {
            if (firedRef.current) return;
            firedRef.current = true;
            io.disconnect();
          }
          onEnter?.();
        }
      },
      { root: null, rootMargin, threshold },
    );
    if (enabled) {
      io.observe(el);
    }
    return () => io.disconnect();
  }, [onEnter, once, rootMargin, threshold, enabled]);

  return ref;
};
